'use client';

import { useState, useEffect, useCallback, memo } from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { useDynamicTheme } from '@/components/DynamicThemeProvider';
import { useTheme } from '@/components/ThemeProvider';
import { getDeals, getStores } from '@/services/api';
import PromoModal from '@/components/coupon/PromoModal';
import { getServerUrl, getImageUrl } from '@/utils/serverUrl';

const OfferCard = memo(function OfferCard({ offer, primary, textColor, mutedText, cardBg, borderClr, isDark, darkBg, redeemLabel, onRedeem }: any) {
  const handleRedeem = () => {
    if (offer.link) window.open(offer.link, '_blank', 'noopener,noreferrer');
    onRedeem(offer);
  };

  return (
    <div className="rounded-2xl overflow-hidden cursor-pointer group shadow-sm hover:shadow-xl transition-all duration-300 border"
      style={{ backgroundColor: cardBg, borderColor: borderClr, height: 320 }} onClick={handleRedeem}>
      <div className="relative overflow-hidden shrink-0 h-[160px] group-hover:h-[80px] transition-all duration-300"
        style={{ backgroundColor: isDark ? darkBg : '#f3f4f6' }}>
        {offer.image && <img src={getImageUrl(offer.image, { width: 640 })} alt={offer.brandName} loading="lazy" decoding="async" className="w-full h-full object-fill" />}
      </div>
      {offer.logo && (
        <div className="relative z-10 px-4 -mt-[18px]">
          <div className="w-10 h-10 rounded-full shadow-md border overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: borderClr }}>
            <img src={getImageUrl(offer.logo, { width: 96 })} alt={offer.brandName} loading="lazy" decoding="async" className="w-full h-full object-fill" />
          </div>
        </div>
      )}
      <div className="px-5 pt-2 pb-4 flex flex-col" style={{ height: offer.logo ? 'calc(100% - 160px + 18px)' : 'calc(100% - 160px)' }}>
        <h3 className="font-bold text-base uppercase tracking-wide mb-1 group-hover:text-sm group-hover:mb-0.5 transition-all duration-300"
          style={{ color: offer.isExclusive ? primary : textColor }}>{offer.badgeText}</h3>
        <p className="text-sm leading-relaxed line-clamp-2 transition-all duration-300" style={{ color: mutedText }}>
          {offer.description || offer.title}
        </p>
        <div className="flex-1" />
        <div className="overflow-hidden max-h-0 group-hover:max-h-24 transition-all duration-300 ease-in-out">
          <div className="border-t border-dashed mb-3" style={{ borderColor: borderClr }} />
          <button onClick={(e) => { e.stopPropagation(); handleRedeem(); }}
            className="w-full py-2.5 rounded-lg font-extrabold text-sm tracking-widest transition-colors"
            style={{ backgroundColor: primary, color: '#ffffff' }}>{redeemLabel}</button>
        </div>
      </div>
    </div>
  );
});

export default function PopularOffers() {
  const { siteConfig, darkPalette, labels } = useDynamicTheme();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const primary = siteConfig?.theme?.primaryColor || '#7c3aed';
  const textColor = isDark ? darkPalette.text : (siteConfig?.theme?.textColor || '#111827');
  const mutedText = isDark ? `${darkPalette.text}99` : `${textColor}80`;
  const cardBg = isDark ? darkPalette.cardBg : '#ffffff';
  const borderClr = isDark ? `${darkPalette.text}15` : `${textColor}12`;
  const redeemLabel = labels?.homepage?.redeemNow || 'REDEEM NOW';

  const [offers, setOffers] = useState<any[]>([]);
  const [modalData, setModalData] = useState<any>(null);

  useEffect(() => {
    const serverUrl = getServerUrl();
    const fix = (u: string) => (u ? (u.startsWith('http') ? u : `${serverUrl}${u}`) : '');
    Promise.all([
      getDeals({ section: 'popular_offers', limit: 8 }).catch(() => ({ data: [] })),
      getStores().catch(() => ({ data: [] })),
    ]).then(([dealRes, storeRes]: any[]) => {
      const dealData = dealRes.data?.data ?? dealRes.data ?? [];
      const dealOffers = (Array.isArray(dealData) ? dealData : []).map((d: any) => ({
        title: d.title || '', description: d.description || '', badgeText: d.discount || 'DEAL', image: d.image || '',
        logo: d.logo || fix(d.store?.logo || ''), brandName: d.store?.storeName || '', isExclusive: d.isFeatured || false,
        link: d.link || d.store?.websiteUrl || '', code: d.couponCode || '',
        discount: d.discount || '', storeName: d.store?.storeName || '',
        details: d.description || '', expiryDate: d.expiryDate || '',
      }));

      // Stores placed in the "Popular Offers of the Day" homepage section
      const storeData = storeRes.data?.data ?? storeRes.data ?? [];
      const storeOffers = (Array.isArray(storeData) ? storeData : [])
        .filter((s: any) => s.section === 'popular_offers' && s.isActive !== false)
        .map((s: any) => ({
          title: s.storeName || '', description: s.details || s.description || '', badgeText: s.discount || 'DEAL',
          image: fix(s.displayImage || ''), logo: fix(s.brandLogo || s.logo || ''),
          brandName: s.storeName || '', isExclusive: s.isFeatured || s.exclusive || false,
          link: s.productUrl || s.affiliateUrl || s.websiteUrl || '', code: s.code || '',
          discount: s.discount || '', storeName: s.storeName || '',
          details: s.details || s.description || '', expiryDate: s.expiryDate || '',
        }));

      // Show admin-curated store offers first, then deals
      setOffers([...storeOffers, ...dealOffers].slice(0, 12));
    }).catch(() => {});
  }, []);

  if (offers.length === 0) return null;

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold" style={{ color: textColor }}>
            {labels?.homepage?.popularOffersTitle || 'Popular Offers of the Day'}
          </h2>
          <Link href="/deals" className="flex items-center gap-1 text-sm font-bold no-underline transition-opacity hover:opacity-80" style={{ color: primary }}>
            View More<ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {offers.slice(0, 4).map((offer, i) => (
            <OfferCard key={i} offer={offer} primary={primary} textColor={textColor} mutedText={mutedText}
              cardBg={cardBg} borderClr={borderClr} isDark={isDark} darkBg={darkPalette.bg}
              redeemLabel={redeemLabel} onRedeem={setModalData} />
          ))}
        </div>
      </section>
      {modalData && (
        <PromoModal onClose={() => setModalData(null)} title={modalData.title} code={modalData.code}
          discount={modalData.discount} storeName={modalData.storeName || modalData.brandName}
          storeLogo={modalData.logo} storeUrl={modalData.link} expiryDate={modalData.expiryDate} details={modalData.details} />
      )}
    </>
  );
}
